import { useMemo, useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useForm, Controller } from 'react-hook-form';
import * as yup from 'yup';
import InboxTemplateMenu from '@features/InboxTemplateMenu';
import { ModalConfirm } from '@components/Modal';
import { Input } from '@components/Input';
import { Select } from '@components/Select';
import TextEditor from '@components/TextEditor';
import {
    useTranslation,
    useYupValidationResolver,
    useChannels,
    useLocations,
    useWhatsAppBusinessTemplates,
} from '@hooks';
import { CHANNEL } from '@constants/channels';
import { MAX_LENGTH } from '@constants/campaigns';
import { alertActions, channelsActions, contactsActions } from '@actions';
import { ConversationStatus, ConversationProperty } from '@constants/conversations';
import { getUserContacts } from '@helpers/userName';
import { handleApiError } from '@helpers/api';
import { parseTipTapJSONToPlainText, testEditorLength } from '@helpers/text-editor';
import { conversationsService } from '@services';
import { Content, Modal } from './styles';

const InitConversation = ({ onInitConversationCallback }) => {
    const [resolveModal, setResolveModal] = useState({ open: false, conversations: [] });
    const [channelType, setChannelType] = useState(CHANNEL.SMS);

    const locations = useSelector(state => state.account?.account?.companies || []);
    const channels = useSelector(state => state.channels.data);
    const modal = useSelector(state => state.contacts.contactInitConversationModal);

    const dispatch = useDispatch();
    const history = useHistory();
    const { t } = useTranslation();
    const { getSelectLocationsOptions } = useLocations();
    const { getChannelOptions, getChannelsByLocationId, checkTwoWaySms, checkWhatsAppStatus, checkTwoWayLiveChat } =
        useChannels();
    const { getWhatsAppBusinessTemplatesOptionsByChannel } = useWhatsAppBusinessTemplates();
    const locationsOptions = useMemo(() => {
        const filterCb = c => modal.locations.includes(c.companyId);
        return getSelectLocationsOptions(filterCb);
    }, [locations, modal.locations]);

    const InitConversationSchema = yup.object().shape({
        contactId: yup.string().required('validation.required'),
        message: yup.string().when('channelId', {
            is: channelId => channelId && channelType !== CHANNEL.WHATSAPP_BUSINESS,
            then: yup
                .string()
                .required('validation.required')
                .test('text-editor-length', `text-editor-length`, value =>
                    testEditorLength(value, MAX_LENGTH[channelType]),
                ),
            otherwise: yup.string().notRequired(),
        }),
        channelId: yup.string().required('validation.required'),
        locationId: yup.number().required('validation.required'),
        whatsAppTemplateId: yup.string().when('channelId', {
            is: channelId => channelId && channelType === CHANNEL.WHATSAPP_BUSINESS,
            then: yup.string().required('validation.required'),
            otherwise: yup.string().notRequired(),
        }),
    });

    const resolver = useYupValidationResolver(InitConversationSchema);

    const {
        setValue,
        control,
        handleSubmit,
        watch,
        formState: { isValid, isDirty, isSubmitting },
    } = useForm({
        mode: 'all',
        resolver,
        defaultValues: {
            contactId: getUserContacts(
                {
                    firstName: modal.ctx?.firstName,
                    lastName: modal.ctx?.lastName,
                    phoneNumber: modal.ctx?.phoneNumber,
                    email: modal.ctx?.email,
                },
                '-',
            ),
            message: '',
            channelId: '',
            locationId: modal.locations[0] || '',
            ...(modal.ctx?.surveyResponseId ? { surveyResponseId: modal.ctx.surveyResponseId } : {}),
            whatsAppTemplateId: '',
        },
    });

    const locationId = watch('locationId');
    const selectedChannelId = watch('channelId');

    useEffect(() => {
        if (modal?.open) {
            dispatch(channelsActions.getWhatsAppBusinessTemplates());
        }
    }, [dispatch, modal?.open]);

    const channelsOptions = useMemo(() => {
        const filterCb = c =>
            checkTwoWaySms(c) ||
            checkWhatsAppStatus(c) ||
            checkTwoWayLiveChat(c) ||
            c.type === CHANNEL.WHATSAPP_BUSINESS;
        const availableChannels = getChannelsByLocationId(locationId, filterCb);
        return getChannelOptions(
            { in: true, out: true, contact: { phoneNumber: modal.ctx?.phoneNumber } },
            availableChannels,
        );
    }, [locationId, channels]);

    const whatsappTemplatesOptions = useMemo(() => {
        return getWhatsAppBusinessTemplatesOptionsByChannel(selectedChannelId, { hasButtons: false });
    }, [selectedChannelId]);

    const onCloseModal = () =>
        dispatch(
            contactsActions.setContactInitConversationModal({ open: false, contactId: null, companies: [], ctx: null }),
        );

    const onInitConversation = async values => {
        try {
            await conversationsService.create({
                ...values,
                message: channelType === CHANNEL.WHATSAPP_BUSINESS ? '' : parseTipTapJSONToPlainText(values.message),
                contactId: modal.contactId,
            });
            dispatch(contactsActions.getContact(modal.contactId));
            if (onInitConversationCallback) {
                onInitConversationCallback();
            }
            dispatch(alertActions.success(t('alertMessages.sendSuccess')));
            onCloseModal();
        } catch (error) {
            if (error.errorCode === 'open_conversation_exists') {
                setResolveModal({ open: true, conversations: error.conversations });
                return;
            }
            handleApiError(error, 'alertMessages.sendFail');
            if (error.errorCode === 'channel_configuration_error') {
                setChannelType(null);
                setValue('whatsAppTemplateId', '');
                setValue('channelId', '', { shouldDirty: true });
                dispatch(channelsActions.get());
            }
        }
    };

    const onGoToConversation = () => {
        history.push(`/messaging/inbox?conversationId=${resolveModal.conversations[0]}`);
        onCloseModal();
    };

    const onResolveConversation = async () => {
        try {
            await conversationsService.editBulk({
                selectedConversations: resolveModal.conversations,
                property: ConversationProperty.Status,
                status: ConversationStatus.Closed,
                selectByFilter: false,
                filter: null,
            });
            dispatch(alertActions.success(t('alertMessages.updateSuccess')));
            setResolveModal({ open: false, conversations: [] });
            dispatch(contactsActions.getContact(modal.contactId));
            if (onInitConversationCallback) {
                onInitConversationCallback();
            }
        } catch (error) {
            handleApiError(error);
        }
    };

    return (
        <>
            <Modal
                isOpen={modal?.open}
                handleClose={onCloseModal}
                title={t('InitConversation.newConversation')}
                onPrimaryAction={handleSubmit(onInitConversation)}
                primaryActionText={t('buttons.create')}
                primaryActionDisabled={isSubmitting || !isValid || !isDirty}
                onSecondaryAction={onCloseModal}
                secondaryActionText={t('buttons.cancel')}
            >
                <Content>
                    <Controller
                        control={control}
                        name="contactId"
                        render={({ field, fieldState: { error }, ref }) => (
                            <Input
                                ref={ref}
                                inputProps={{ ...field, onChange: e => field.onChange(e.target.value) }}
                                label={t('InitConversation.recipient')}
                                fullWidth
                                error={!!error}
                                helperText={error ? t(error.message) : null}
                                required
                                disabled
                                sx={{ gridArea: 'input1' }}
                            />
                        )}
                    />
                    <Controller
                        control={control}
                        name="locationId"
                        render={({ field, fieldState: { error }, ref }) => (
                            <Select
                                {...field}
                                ref={ref}
                                onChange={({ target: { value } }) => {
                                    setValue('locationId', value, { shouldValidate: true, shouldDirty: true });
                                    setValue('channelId', '', { shouldDirty: true });
                                    setValue('whatsAppTemplateId', '');
                                }}
                                options={locationsOptions}
                                label={t('common.location')}
                                fullWidth
                                style={{ gridArea: 'select1' }}
                                error={!!error}
                                helperText={error ? t(error.message) : null}
                                required
                                disabled={modal.locations.length === 1}
                                groupBased
                            />
                        )}
                    />
                    <Controller
                        control={control}
                        name="channelId"
                        render={({ field, fieldState: { error }, ref }) => (
                            <Select
                                {...field}
                                onChange={({ target: { value } }) => {
                                    const channel = channels.find(c => c.id === value);
                                    setChannelType(channel?.type || CHANNEL.SMS);
                                    setValue('channelId', value, { shouldValidate: true, shouldDirty: true });
                                    setValue('whatsAppTemplateId', '');
                                }}
                                ref={ref}
                                label={t('common.channel')}
                                fullWidth
                                options={channelsOptions}
                                required
                                style={{ gridArea: 'select2' }}
                                error={!!error}
                                helperText={error ? t(error.message) : null}
                            />
                        )}
                    />
                    {channelType === CHANNEL.WHATSAPP_BUSINESS && (
                        <Controller
                            control={control}
                            name="whatsAppTemplateId"
                            render={({ field, fieldState: { error }, ref }) => (
                                <Select
                                    {...field}
                                    ref={ref}
                                    onChange={({ target: { value } }) => {
                                        setValue('whatsAppTemplateId', value, {
                                            shouldDirty: true,
                                            shouldValidate: true,
                                        });
                                    }}
                                    options={whatsappTemplatesOptions}
                                    required
                                    label={t('FeedbackOverview.template')}
                                    fullWidth
                                    style={{ gridArea: 'input2' }}
                                    error={!!error}
                                    helperText={error ? t(error.message) : null}
                                />
                            )}
                        />
                    )}
                    {channelType !== CHANNEL.WHATSAPP_BUSINESS && (
                        <Controller
                            control={control}
                            name="message"
                            render={({ field, fieldState: { error }, ref }) => (
                                <div style={{ gridArea: 'input2' }}>
                                    <TextEditor
                                        {...field}
                                        ref={ref}
                                        label={t('common.message', { count: 1 })}
                                        limit={MAX_LENGTH[channelType]}
                                        initialValue={''}
                                        onChange={text =>
                                            setValue('message', text, {
                                                shouldValidate: true,
                                                shouldDirty: true,
                                            })
                                        }
                                        error={error}
                                        aiAssistant
                                        toolbar={
                                            <div style={{ display: 'flex', gap: '6px' }}>
                                                <InboxTemplateMenu
                                                    content={field.value}
                                                    setContent={text =>
                                                        setValue('message', text, {
                                                            shouldValidate: true,
                                                            shouldDirty: true,
                                                        })
                                                    }
                                                    tags={{
                                                        firstName: modal.ctx?.firstName,
                                                        lastName: modal.ctx?.lastName,
                                                        locations: modal.locations,
                                                    }}
                                                />
                                            </div>
                                        }
                                    />
                                </div>
                            )}
                        />
                    )}
                </Content>
            </Modal>
            {resolveModal.open && (
                <ModalConfirm
                    isOpen={resolveModal.open}
                    handleClose={() => setResolveModal({ open: false, conversationId: '' })}
                    title={t('InitConversation.conversationExistsTitle')}
                    description={t('InitConversation.conversationExistsDescription')}
                    onPrimaryAction={onGoToConversation}
                    primaryActionText={t('InitConversation.goToConversation')}
                    onSecondaryAction={onResolveConversation}
                    secondaryActionText={t('InitConversation.resolveConversation')}
                />
            )}
        </>
    );
};

export default InitConversation;
